import { logger } from '@adornis/base/logging.js';
import { Float } from '@adornis/baseql/baseqlTypes.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import * as crypto from 'node:crypto';
import { splitEvaluationResult, Submission } from '../submission.js';

export const createSubmissionMutation = registerMutation({
  operationName: 'createSubmissionMutation',
  type: () => Submission,
  params: [
    { name: 'examId', type: () => String },
    { name: 'imageIds', type: () => [String] },
    { name: 'studentName', type: () => String },
    { name: 'splitConfidence', type: () => Float },
    { name: 'splitIssues', type: () => [String] },
    { name: 'guestUserID', type: () => String },
    { name: 'splitJobId', type: () => String },
  ],

  resolve:
    (
      examId: string,
      imageIds: string[],
      studentName: string,
      splitConfidence?: number,
      splitIssues?: string[],
      guestUserID?: string,
      splitJobId?: string,
    ) =>
    async (_gqlFields: BaseQLSelectionSet<Submission>) => {
      const submissionId = crypto.randomUUID();

      const timestamp = new Date().toISOString();
      const normalizedSplitConfidence = Number.isFinite(Number(splitConfidence)) ? Number(splitConfidence) : 0;
      const normalizedSplitIssues = Array.isArray(splitIssues) ? splitIssues : [];

      // Get the current user's ID for ownership
      // NON-FATAL, because this mutation is no longer only reached from a request. The `submissionSplit` job
      // calls it with no session, and `getMyself` does not resolve to null there — it THROWS ('The server does
      // not have a user…'), which failed the whole split after the agent and the page encode had already run.
      // The owner a background caller means is the one it passes in; resolving the session is the fallback,
      // not the source of truth.
      const currentUser = await CurrentUserInfo.getMyself()({ _id: 1 }).catch(() => null);
      const userID = currentUser?._id || guestUserID || null;
      const submission = new Submission({
        _id: submissionId,
        examId,
        submittedAt: timestamp,
        imageIds,
        userID,
        studentName,
        // Newly created submissions are editable until the split is confirmed.
        splitConfirmed: false,
        splitEvaluationResult: new splitEvaluationResult({
          confidence: normalizedSplitConfidence,
          issues: normalizedSplitIssues,
        }),
        ...(splitJobId ? { splitJobId } : {}),
      });

      const submissionCollection = await getCollection(Submission);
      const submissionData = submission.toObject() as any;
      submissionData.splitEvaluationResult = {
        _class: splitEvaluationResult._class,
        confidence: normalizedSplitConfidence,
        issues: normalizedSplitIssues,
      };

      await submissionCollection.insertOne(submissionData);
      logger.info({ submissionId, examId, studentName }, 'Created submission');
      // Return the created Submission entity (BaseQL will handle selection)
      return submission;
    },
});
